import type { ShareAsset } from "~/types/assets.types";
import type { Fiat } from "~/types/global.types";
import { calculateOwnership } from "~/utils/calculations.utils";
import { convertCurrency } from "../genericCalculations/convertCurrency";


const CAPITAL_TAX_RATE = 0.25
const YESEF_TAX_RATE = 0.03
const EXTRA_YESEF_FOR_CAPITAL_GAINS_2025 = 0.02

export const calculateStocksTotalBalance = (
  userProduct: ShareAsset,
  withCash: boolean,
  cashCurrency: Fiat,
  baseCurrency: Fiat
) => {
  const cash = withCash
    ? convertCurrency(
        Number(userProduct?.cash) || 0,
        baseCurrency,
        cashCurrency
      )
    : 0;
  const stocks = userProduct?.stocksData || [];
  const { balanceInBaseCurrency: shareBalances } =
    calculateShareBalance(userProduct);

  return stocks.reduce((sum, _, index) => {
    const shareBalance = shareBalances?.[index] || 0;

    return sum + shareBalance;
  }, cash);
};

export const calculateStocksTotalBalanceOwnership = (
  userProduct: ShareAsset,
  cashCurrency: Fiat,
  BaseCurrency: Fiat
) => {
  const totalBalance = calculateStocksTotalBalance(
    userProduct,
    true,
    cashCurrency,
    BaseCurrency
  );
  const ownership = userProduct?.ownership || 0;

  return calculateOwnership(totalBalance, ownership);
};



export const convertShareBalance = (
  userProduct: ShareAsset,
  currencies: {
    baseCurrency: Fiat;
    cashCurrency: Fiat;
  },
  withOnwership: boolean
) => {
  const { baseCurrency, cashCurrency } = currencies;
  const ownership = userProduct?.ownership || 0;
  const balance = calculateStocksTotalBalance(
    userProduct,
    true,
    cashCurrency,
    baseCurrency
  );
  const balanceOwnership = withOnwership
    ? calculateOwnership(balance, ownership)
    : balance;
  return balanceOwnership;
};

export const calculateShareBalance = (userProduct: ShareAsset) => {
  /*
    returns two arrays:
    1. balance in base currency (default ILS)
    2. balance in share currency (the currency of the stock)
  */
  const stocks = userProduct?.stocksData || [];

  const [balanceInBaseCurrency, balanceInShareCurrency]: [number[], number[]] =
    [[], []];
  const totalCapitalLosses = (userProduct?.offsetLosses ? stocks.reduce((sum, stock) => 
  {
    const stockPriceBase = stock?.stockPriceInBaseCurrency || 0;
    const stockPrice = stock?.stockPrice || 0;
    const purchasePriceBase = (stock?.purchasePrice || 0) * (stockPriceBase / stockPrice || 1);
    // "כספית" = money market fund — exempt from capital gains/losses calculation
    const gain = stock.stockName?.includes("כספית") ? 0 : Math.min(stockPriceBase - purchasePriceBase, 0);
    return sum + gain;
  }, 0
  ) : 0);
  const totalCapitalGains = (userProduct?.offsetLosses ? stocks.reduce((sum, stock) =>
  {
    const stockPriceBase = stock?.stockPriceInBaseCurrency || 0;
    const stockPrice = stock?.stockPrice || 0;
    const purchasePriceBase = (stock?.purchasePrice || 0) * (stockPriceBase / stockPrice || 1);
    return sum + Math.max(stockPriceBase - purchasePriceBase, 0);
  }, 0
  ) : 0);
  const taxRate = userProduct?.showAfterTax ? (CAPITAL_TAX_RATE + (userProduct?.yesefTax ? YESEF_TAX_RATE + EXTRA_YESEF_FOR_CAPITAL_GAINS_2025 : 0)) : 0;
  //now we want to reduce the tax Rate by the proportion of capital losses from capital gains if losses > gains tax rate should be zero
  //else tax rate should be reduced according to the percantage tax losses arefrom tax gains
  let taxRateAfterLosses = Math.max((taxRate - (Math.abs(totalCapitalLosses) / totalCapitalGains) * taxRate), 0);
  const finalTaxRate = userProduct?.offsetLosses ? taxRateAfterLosses : taxRate;
  stocks.forEach((stock) => {
    const amount = stock?.amount || 0;
    const price = stock?.stockPrice || 0;
    // "כספית" = money market fund — not subject to capital gains tax
    const taxablePrice = stock.stockName?.includes("כספית") ? 0 : Math.max(price - (stock?.purchasePrice || 0), 0);
    const priceInBaseCurrency = stock?.stockPriceInBaseCurrency || 0;
    balanceInShareCurrency.push(amount * price - (amount * taxablePrice * finalTaxRate));
    balanceInBaseCurrency.push(amount * priceInBaseCurrency - (amount * taxablePrice * finalTaxRate * (priceInBaseCurrency / price || 1)));
  });

  return { balanceInBaseCurrency, balanceInShareCurrency };
};

export const calculateShareCurrencyBalance = (
  userProduct: ShareAsset,
  baseCurrency: Fiat,
  currentCurrency: Fiat
) => {
  const stocks = userProduct?.stocksData || [];
  const { balanceInBaseCurrency: shareBalances } =
    calculateShareBalance(userProduct);

  return stocks.map((_, index) => {
    const shareBalance = shareBalances?.[index] || 0;

    return convertCurrency(shareBalance, baseCurrency, currentCurrency);
  });
};

export const calculateStocksUserCurrencyBalanceWithoutOwnership = (
  userProduct: ShareAsset,
  baseCurrency: Fiat,
  currentCurrency: Fiat
) => {
  const stocks = userProduct?.stocksData || [];
  const cash = userProduct?.cash || 0;
  const shareCurrencyBalances = calculateShareCurrencyBalance(
    userProduct,
    baseCurrency,
    currentCurrency
  );

  return stocks.reduce((sum, _, index) => {
    const shareCurrencyBalance = shareCurrencyBalances?.[index] || 0;
    return sum + shareCurrencyBalance * cash;
  }, 0);
};

export const calculateStocksSumCurrencyBalance = (userProduct: ShareAsset) => {
  const stocks = userProduct?.stocksData || [];
  const { balanceInBaseCurrency: currencyBalances } =
    calculateShareBalance(userProduct);

  return stocks.reduce((sum, _, index) => {
    const currencyBalance = currencyBalances?.[index] || 0;

    return sum + currencyBalance;
  }, 0);
};


